import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { ExecutiveTrendPoint } from '@ace-tracker/shared';
import { chartAxisTickProps, chartTooltipProps, chartXAxisTickProps } from '../../chartTheme';
import { TOOL_COLORS } from '../../toolColors';
import { formatWeek } from './format';

const SERIES = [
  { key: 'gemini', name: 'Gemini CLI', color: TOOL_COLORS.gemini, width: 1.8 },
  { key: 'kiro', name: 'AWS Kiro', color: TOOL_COLORS.kiro, width: 1.8 },
  { key: 'codex', name: 'OpenAI Codex', color: TOOL_COLORS.codex, width: 1.8 },
  { key: 'claudeCode', name: 'Claude Code', color: TOOL_COLORS['claude-code'], width: 1.8 },
  { key: 'copilot', name: 'GitHub Copilot', color: TOOL_COLORS.copilot, width: 1.8 },
  { key: 'allTools', name: 'All tools (unique)', color: 'var(--text-default)', width: 2.8 },
] as const;

/** Rounds the axis top up to a tidy step so the target line and every value sit inside the chart. */
function niceMax(value: number): number {
  if (value <= 10) return 10;
  const step = 10 ** Math.floor(Math.log10(value)) / 2;
  return Math.ceil((value * 1.08) / step) * step;
}

/**
 * Rolling 28-day active developers, weekly for 12 weeks: a bold unique line,
 * a thinner line per tool, and the adoption target. The legend carries the
 * latest values, so the chart reads without hovering.
 */
export function ExecutiveTrendChart({ points, target }: { points: ExecutiveTrendPoint[]; target: number | null }) {
  const data = points.map((p) => ({ ...p, label: formatWeek(p.weekEnding) }));
  const last = points[points.length - 1];
  const peak = Math.max(target ?? 0, ...points.map((p) => p.allTools));
  const legend = [...SERIES].reverse();

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} accessibilityLayer>
            <CartesianGrid vertical={false} stroke="var(--line-soft)" />
            <XAxis dataKey="label" {...chartXAxisTickProps} axisLine={false} interval="preserveStartEnd" minTickGap={18} />
            <YAxis {...chartAxisTickProps} axisLine={false} width={36} allowDecimals={false} domain={[0, niceMax(peak)]} />
            <Tooltip {...chartTooltipProps} cursor={{ stroke: 'var(--border)' }} />
            {target !== null && (
              <ReferenceLine
                y={target}
                stroke="var(--text-muted)"
                strokeDasharray="5 4"
                label={{ value: `Target ${target}`, position: 'insideTopLeft', fill: 'var(--text-muted)', fontSize: 11 }}
              />
            )}
            {SERIES.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={s.width}
                dot={false}
                activeDot={{ r: 3 }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-text-muted" aria-label="Latest values">
        {legend.map((s) => (
          <li key={s.key} className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-block h-[3px] w-3.5 rounded" style={{ backgroundColor: s.color }} />
            {s.name}
            {last && <b className="font-bold tabular-nums text-text-default">{last[s.key]}</b>}
          </li>
        ))}
        {target !== null && (
          <li className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-block h-0 w-3.5 border-t-2 border-dashed border-text-muted" />
            Target <b className="font-bold tabular-nums text-text-default">{target}</b>
          </li>
        )}
      </ul>
    </div>
  );
}
